import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { 
  Rocket, Calendar, Gift, Lock, Loader2, 
  LayoutGrid, Menu, X, Sparkles, Tag, User, Percent 
} from 'lucide-react';
import SubscriptionModal from './SubscriptionModal';
import Footer from './Footer';
import './PricingCard.css';

const PricingCard = ({ darkMode }) => {
  const navigate = useNavigate();

  const [lessons, setLessons] = useState([]);
  const [offers, setOffers] = useState([]); // العروض الجديدة
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [deviceFingerprint, setDeviceFingerprint] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [errorMessages, setErrorMessages] = useState({});
  const [filter, setFilter] = useState("lecture"); 
  const [subscribedPackageIds, setSubscribedPackageIds] = useState([]);

  // التحكم في قائمة الموبايل
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ====== جلب البيانات (الباقات + الاشتراكات + العروض) ======
  const fetchData = async () => {
    setLoading(true);
    try {
      const educationalLevelId = localStorage.getItem('educationalLevelId');
      const authToken = localStorage.getItem('authToken');
      const Teacher_id = localStorage.getItem("teacher_id");

      if (!authToken || !educationalLevelId) {
        navigate('/Signin');
        return;
      }

      // 1. طلب الباقات العادية
      const packagesPromise = axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/getPackages/${Teacher_id}/${educationalLevelId}`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      // 2. طلب اشتراكات المستخدم
      const subscriptionsPromise = axios.get(
        'https://leader-acadmy.hwnix.com/api/user/subscriptions',
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      // 3. طلب العروض (الـ API الجديد)
      const offersPromise = axios.get(
        `https://leader-acadmy.hwnix.com/api/offers/educational-level/${educationalLevelId}`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      const [packagesRes, subsRes, offersRes] = await Promise.all([
        packagesPromise, 
        subscriptionsPromise,
        offersPromise
      ]);

      setLessons(packagesRes.data.packages || []);
      setOffers(offersRes.data.offers || []);

      if (subsRes.data?.status && subsRes.data.packages) {
        const subIds = subsRes.data.packages.map(pkg => pkg.id);
        setSubscribedPackageIds(subIds);
      }

    } catch (err) {
      setError(err);
      console.error("Fetch Error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchFingerprint = async () => {
      try {
        const fp = await FingerprintJS.load();
        const result = await fp.get();
        setDeviceFingerprint(result.visitorId);
        localStorage.setItem('deviceFingerprint', result.visitorId);
      } catch (err) {
        navigate('/login');
      }
    };
    fetchFingerprint();
    fetchData(); 
  }, [navigate]);

  // منطق الدخول والتحقق
  const handleActionClick = async (lesson, path) => {
    if (!lesson) return;
    localStorage.setItem('package_id', lesson.id);
    const id = localStorage.getItem('id');
    const subscriptionCode = localStorage.getItem('subscriptionCode');
    const deviceFingerprint = localStorage.getItem('deviceFingerprint');

    try {
      const response = await axios.get(
        `https://leader-acadmy.hwnix.com/api/code/check/${id}/${deviceFingerprint}/${lesson.id}?code=${subscriptionCode}`
      );

      if (response.data.message === 'User has a valid code.') {
        navigate(path);
      } else {
        setErrorMessages(prev => ({ ...prev, [lesson.id]: 'الكود غير صالح أو يوجد مشكلة.' }));
      }
    } catch (err) {
      const msg = err.response?.data?.message;
      let errorText = 'من فضلك ادخل كود الاشتراك أولاً';
      if (msg === 'MAC address mismatch.') errorText = 'برجاء الدخول من الجهاز الأصلي';
      setErrorMessages(prev => ({ ...prev, [lesson.id]: errorText }));
    }
  };

  const handleSubscribeClick = lesson => {
    localStorage.setItem('package_id', lesson.id);
    setSelectedLesson(lesson);
    setIsModalOpen(true);
  };

  // تصفية المحاضرات والشهور فقط (العروض لها logic منفصل)
  const filteredLessons = lessons.filter(lesson => {
    const title = lesson.title.toLowerCase();
    if (filter === "month") return title.includes("شهر");
    if (filter === "lecture") return title.includes("محاضر");
    return false;
  });

  const renderNavButtons = () => (
    <>
      <button className={`nav-pill ${filter === "month" ? "active" : ""}`} onClick={() => { setFilter("month"); setIsMenuOpen(false); }}>
        <Calendar size={18} /> <span>الشهور</span>
      </button>
      <button className={`nav-pill ${filter === "lecture" ? "active" : ""}`} onClick={() => { setFilter("lecture"); setIsMenuOpen(false); }}>
        <Rocket size={18} /> <span>المحاضرات</span>
      </button>
      <button className={`nav-pill pill-offer ${filter === "offer" ? "active" : ""}`} onClick={() => { setFilter("offer"); setIsMenuOpen(false); }}>
        <Gift size={18} /> <span>العروض</span>
      </button>
      <div className="nav-divider"></div>
      <button className="nav-pill btn-special" onClick={() => { navigate("/MySubscriptions"); setIsMenuOpen(false); }}>
        <LayoutGrid size={18} /> <span>اشتراكاتي</span>
      </button>
      <button className="nav-pill btn-special" onClick={() => { navigate("/Teachers"); setIsMenuOpen(false); }}>
        <User size={18} /> <span>المدرسين</span>
      </button>
    </>
  );

  return (
    <div className={`pricing-page-master ${darkMode ? 'dark-mode' : ''}`}>
      
      {/* هيدر الفلترة */}
      <div className="filter-nav-bar">
        <div className="filter-inner">
          {isMobile ? (
            <div className="mobile-menu-container">
              <button className="mobile-menu-btn" onClick={() => setIsMenuOpen(!isMenuOpen)}>
                {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
                <span>القائمة</span>
              </button>
              <AnimatePresence>
                {isMenuOpen && (
                  <motion.div 
                    className="mobile-dropdown"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                  >
                    {renderNavButtons()}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ) : (
            <div className="desktop-nav-buttons">
              {renderNavButtons()}
            </div>
          )}
        </div>
      </div>

      <div className="container-main">
        {loading ? (
          <div className="loader-center">
            <Loader2 className="spinner-icon" size={48} />
            <p>جاري تحضير المحتوى...</p>
          </div>
        ) : (
          <div className="content-grid-layout">
            <AnimatePresence mode="wait">
              <motion.div
                key={filter}
                className={filter === 'offer' ? "offers-premium-grid" : "cards-3-column-grid"}
                initial={{ opacity: 0, x: 15 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -15 }}
                transition={{ duration: 0.3 }}
              >
                {/* --- عرض قسم العروض المميزة --- */}
                {filter === 'offer' ? (
                  offers.length > 0 ? (
                    offers.map((offer) => (
                      <div key={offer.id} className="premium-offer-card">
                        <div className="offer-glow"></div>
                        <div className="offer-tag"><Sparkles size={14} /> عرض حصري</div>
                        
                        <div className="offer-body">
                          <div className="offer-header-flex">
                             <div className="discount-badge">
                               <span className="num">{offer.discount_percentage}%</span>
                               <span className="txt">خصم</span>
                             </div>
                             <div className="offer-title-group">
                               <h3>{offer.title}</h3>
                               <div className="offer-teacher-info">
                                 <User size={14} />
                                 <span>{offer.teacher?.name}</span>
                               </div>
                             </div>
                          </div>
                          
                          <p className="offer-desc-text">{offer.description}</p>
                          
                          <div className="offer-features">
                            <div className="feat-item"><Tag size={14} /> {offer.teacher?.subject?.name}</div>
                            <div className="feat-item"><Calendar size={14} /> متاح لفترة محدودة</div>
                          </div>
                        </div>

                        <div className="offer-actions">
                          
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="empty-state">
                      <Gift size={64} opacity={0.2} />
                      <p>لا توجد عروض متاحة حالياً لهذا المستوى</p>
                    </div>
                  )
                ) : (
                  /* --- عرض المحاضرات والشهور --- */
                  <>
                    {filteredLessons.map((lesson) => {
                      const isSubscribed = subscribedPackageIds.includes(lesson.id);
                      return (
                        <div key={lesson.id} className="modern-lesson-card">
                          <div className="card-img-holder">
                            <img
                              src={`https://leader-acadmy.hwnix.com/storage/${lesson.image?.image_url}`}
                              alt={lesson.title}
                            />
                            <div className="glass-tag">{filter === 'month' ? 'باقة شهرية' : 'محاضرة'}</div>
                          </div>

                          <div className="card-content-box">
                            <h3>{lesson.title}</h3>
                            <p className="lesson-desc">{lesson.description}</p>
                            {isSubscribed && <span className="subscribed-badge">✓ تم الاشتراك</span>}
                            {errorMessages[lesson.id] && (
                              <div className="card-alert"><Lock size={14} /> {errorMessages[lesson.id]}</div>
                            )}
                          </div>

                          <div className="card-footer-actions">
                            <button className="btn-enter" onClick={() => handleActionClick(lesson, '/ProfileCard')}>دخول</button>
                            {!isSubscribed && (
                              <button className="btn-sub" onClick={() => handleSubscribeClick(lesson)}>اشتراك</button>
                            )}
                          </div>
                        </div>
                      );
                    })}

                    {/* باقة الترم الثابتة تظهر مع المحاضرات */}
                    {filter === 'lecture' && (
                      <div className="modern-lesson-card term-special-card">
                        <div className="card-img-holder">
                          <img src="/images/saas.png" alt="باقة الترم" />
                          <div className="glass-tag blue">باقة الترم</div>
                        </div>
                        <div className="card-content-box">
                          <h3>باقة الترم الكاملة</h3>
                          <p className="lesson-desc">احصل على كافة محاضرات الترم بخصم خاص</p>
                        </div>
                        <div className="card-footer-actions">
                          <button className="btn-enter" onClick={() => handleActionClick(lessons[0], '/CardTerm')}>دخول</button>
                          <button className="btn-sub" onClick={() => handleSubscribeClick(lessons[0])}>اشتراك</button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        )}
      </div>

      {isModalOpen && (
        <SubscriptionModal
          lesson={selectedLesson}
          onClose={() => setIsModalOpen(false)}
        />
      )}
      <Footer/>
    </div>
  );
};

export default PricingCard;